import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { boardsApi, IDashboardSummary, IDashboardSummaryBoard } from 'src/app/modules/workflow/services/boardsApi';
import { KTSVG } from 'src/_metronic/helpers';

/**
 * Workflow Dashboard Summary Menu
 * Topbar'da bekleyen görevleri ve panoları gösterir
 */
const WorkflowSummaryMenu: React.FC = () => {
  const { data, isLoading, error } = useQuery<IDashboardSummary>(['workflow-dashboard-summary'], () => boardsApi.getDashboardSummary(), {
    staleTime: 1000 * 60 * 5, // 5 dakika cache
    refetchOnWindowFocus: false,
  });

  // Board renkleri için mapping
  const getBoardColorClass = (color: string): string => {
    const colorMap: Record<string, string> = {
      blue: 'primary',
      green: 'success',
      red: 'danger',
      yellow: 'warning',
      purple: 'info',
      gray: 'secondary',
      orange: 'warning',
      pink: 'danger',
      cyan: 'info',
    };
    return colorMap[color] || 'primary';
  };

  // Board rolü için Türkçe çeviri
  const getRoleText = (role: string): string => {
    const roleMap: Record<string, string> = {
      admin: 'Yönetici',
      editor: 'Düzenleyici',
      viewer: 'İzleyici',
      member: 'Üye',
    };
    return roleMap[role] || 'Üye';
  };

  return (
    <div className="menu menu-sub menu-sub-dropdown menu-column w-250px w-lg-325px" data-kt-menu="true">
      {/* Header */}
      <div className="d-flex flex-column flex-center bgi-no-repeat rounded-top px-9 py-10" style={{ backgroundImage: "url('/media/misc/pattern-1.jpg')" }}>
        <h3 className="text-white fw-bold mb-3">Bekleyen Görevlerin</h3>
        {isLoading ? (
          <span className="badge bg-secondary py-2 px-3">Yükleniyor...</span>
        ) : error ? (
          <span className="badge bg-danger py-2 px-3">Hata oluştu</span>
        ) : (
          <span className="badge bg-primary py-2 px-3">{data?.pending_task_count || 0} devam eden görevin var.</span>
        )}
      </div>

      {/* Board List */}
      <div className="row g-0">
        {isLoading ? (
          // Loading skeleton
          <>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="col-6">
                <div className="d-flex flex-column flex-center h-100 p-6 bg-light border-end border-bottom">
                  <div className="w-40px h-40px rounded bg-secondary mb-2 animate-pulse"></div>
                  <div className="w-60px h-15px bg-secondary rounded mb-1 animate-pulse"></div>
                  <div className="w-40px h-10px bg-secondary rounded animate-pulse"></div>
                </div>
              </div>
            ))}
          </>
        ) : data?.boards && data.boards.length > 0 ? (
          // Board cards
          data.boards.map((board: IDashboardSummaryBoard, index: number) => (
            <div key={board.id} className="col-6">
              <Link
                to={`/workflow/boards/${board.id}`}
                data-kt-menu-dismiss="true"
                className={`d-flex flex-column flex-center h-100 p-6 bg-hover-light ${index % 2 === 0 ? 'border-end' : ''} ${index < 2 ? 'border-bottom' : ''}`}
              >
                <KTSVG path="/media/icons/duotone/Text/Bullet-list.svg" className={`svg-icon svg-icon-3x svg-icon-${getBoardColorClass(board.color)} mb-2`} />
                <span className="fs-5 fw-bold text-gray-800 mb-0 text-center" style={{ wordBreak: 'break-word' }}>
                  {board.name.length > 30 ? `${board.name.substring(0, 30)}...` : board.name}
                </span>
                <span className="fs-7 text-gray-400">{board.task_count} görev</span>
              </Link>
            </div>
          ))
        ) : (
          // No boards
          <div className="col-12">
            <div className="d-flex flex-column flex-center py-10 px-6">
              <span className="svg-icon svg-icon-3x svg-icon-muted mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M20 14H18V10H20C20.6 10 21 10.4 21 11V13C21 13.6 20.6 14 20 14ZM21 19V17C21 16.4 20.6 16 20 16H18V20H20C20.6 20 21 19.6 21 19ZM21 7V5C21 4.4 20.6 4 20 4H18V8H20C20.6 8 21 7.6 21 7ZM10 4H8V20H10V4ZM14 4H12V20H14V4ZM6 4H4C3.4 4 3 4.4 3 5V7C3 7.6 3.4 8 4 8H6V4ZM6 10H4C3.4 10 3 10.4 3 11V13C3 13.6 3.4 14 4 14H6V10ZM6 16H4C3.4 16 3 16.4 3 17V19C3 19.6 3.4 20 4 20H6V16Z"
                    fill="currentColor"
                  />
                </svg>
              </span>
              <span className="text-muted fs-6">Henüz bir panoya üye değilsiniz</span>
            </div>
          </div>
        )}

        {/* Empty cells to fill grid if less than 4 boards */}
        {!isLoading && data?.boards && data.boards.length > 0 && data.boards.length < 4 && (
          <>
            {Array.from({ length: 4 - data.boards.length }).map((_, index) => (
              <div key={`empty-${index}`} className="col-6">
                <div
                  className={`d-flex flex-column flex-center h-100 p-6 ${(data.boards.length + index) % 2 === 0 ? 'border-end' : ''} ${
                    data.boards.length + index < 2 ? 'border-bottom' : ''
                  }`}
                  style={{ minHeight: '120px' }}
                />
              </div>
            ))}
          </>
        )}
      </div>

      {/* Footer - View All */}
      <div className="py-2 text-center border-top">
        <Link to="/workflow" data-kt-menu-dismiss="true" className="btn btn-color-gray-600 btn-active-color-primary d-flex flex-row align-items-center justify-content-center">
          Tümünü gör
          {data?.total_boards && data.total_boards > 4 && <span className="badge badge-sm bg-primary ms-2">{data.total_boards}</span>}
          <span className="svg-icon svg-icon-5 ms-1">
            <svg xmlns="http://www.w3.org/2000/svg" width="24px" height="24px" viewBox="0 0 24 24" version="1.1">
              <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                <polygon points="0 0 24 0 24 24 0 24" />
                <rect
                  fill="#000000"
                  opacity="0.5"
                  transform="translate(8.500000, 12.000000) rotate(-90.000000) translate(-8.500000, -12.000000)"
                  x="7.5"
                  y="7.5"
                  width="2"
                  height="9"
                  rx="1"
                />
                <path
                  d="M9.70710318,15.7071045 C9.31657888,16.0976288 8.68341391,16.0976288 8.29288961,15.7071045 C7.90236532,15.3165802 7.90236532,14.6834152 8.29288961,14.2928909 L14.2928896,8.29289093 C14.6714686,7.914312 15.281055,7.90106637 15.675721,8.26284357 L21.675721,13.7628436 C22.08284,14.136036 22.1103429,14.7686034 21.7371505,15.1757223 C21.3639581,15.5828413 20.7313908,15.6103443 20.3242718,15.2371519 L15.0300721,10.3841355 L9.70710318,15.7071045 Z"
                  fill="#000000"
                  fillRule="nonzero"
                  transform="translate(14.999999, 11.999997) scale(1, -1) rotate(90.000000) translate(-14.999999, -11.999997)"
                />
              </g>
            </svg>
          </span>
        </Link>
      </div>
    </div>
  );
};

export { WorkflowSummaryMenu };
